import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Globe, Link2, Copy, Check, Lock, Calendar, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard } from '@/lib/clipboard';
import api from '@/lib/api';

export default function PublishDialog({ note, editor, onClose }) {
  const { updateNoteLocal } = useApp();
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [pubInfo, setPubInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [password, setPassword] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [expiryEnabled, setExpiryEnabled] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');

  const isPublished = pubInfo?.is_published;
  const publicUrl = isPublished ? `${window.location.origin}/published/${pubInfo.token}` : null;

  // Load current publish state
  useEffect(() => {
    if (!note?.id) return;
    (async () => {
      try {
        const res = await api.get(`/notes/${note.id}/publish`);
        setPubInfo(res.data);
        setHasPassword(res.data.has_password || false);
        if (res.data.expires_at) {
          setExpiryEnabled(true);
          setExpiryDate(res.data.expires_at.slice(0, 16));
        }
      } catch {
        setPubInfo({ is_published: false });
      }
      setLoading(false);
    })();
  }, [note?.id]);

  const handlePublish = useCallback(async () => {
    if (!note?.id || !editor) return;
    setPublishing(true);
    try {
      const htmlContent = editor.getHTML();
      const payload = { content: htmlContent };
      if (password) payload.password = password;
      if (expiryEnabled && expiryDate) payload.expires_at = new Date(expiryDate).toISOString();

      const res = await api.post(`/notes/${note.id}/publish`, payload);
      setPubInfo({ is_published: true, ...res.data });
      updateNoteLocal(note.id, { is_published: true, publish_token: res.data.token });
      toast.success('Note published');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    }
    setPublishing(false);
  }, [note?.id, editor, password, expiryEnabled, expiryDate, updateNoteLocal]);

  const handleUnpublish = useCallback(async () => {
    if (!note?.id) return;
    setPublishing(true);
    try {
      await api.delete(`/notes/${note.id}/publish`);
      setPubInfo({ is_published: false });
      updateNoteLocal(note.id, { is_published: false, publish_token: null });
      toast.success('Note unpublished');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpublish');
    }
    setPublishing(false);
  }, [note?.id, updateNoteLocal]);

  const handleUpdateSettings = useCallback(async () => {
    if (!note?.id) return;
    setPublishing(true);
    try {
      const payload = {};
      if (hasPassword && password) {
        payload.password = password;
      } else if (!hasPassword) {
        payload.remove_password = true;
      }
      if (expiryEnabled && expiryDate) {
        payload.expires_at = new Date(expiryDate).toISOString();
      } else if (!expiryEnabled) {
        payload.remove_expiry = true;
      }
      const res = await api.put(`/notes/${note.id}/publish`, payload);
      setPubInfo((prev) => ({ ...prev, ...res.data }));
      setHasPassword(res.data.has_password);
      setPassword('');
      toast.success('Publish settings updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    }
    setPublishing(false);
  }, [note?.id, hasPassword, password, expiryEnabled, expiryDate]);

  const copyLink = async () => {
    if (!publicUrl) return;
    const result = await copyToClipboard(publicUrl);
    if (result.success) {
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy — try selecting and copying manually');
    }
  };

  if (loading) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="publish-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            {isPublished ? 'Publish Settings' : 'Publish Note'}
          </DialogTitle>
          <DialogDescription>
            {isPublished
              ? 'This note is live. All edits are published immediately.'
              : 'Make this note publicly viewable as a web page.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Published state — link + settings */}
          {isPublished && (
            <>
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-green-500/10 border border-green-500/20">
                <Globe className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="text-xs text-green-400 font-medium">Published — changes are live</span>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Public URL</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={publicUrl}
                    className="text-xs bg-secondary/50 flex-1"
                    data-testid="publish-url"
                  />
                  <Button size="sm" variant="outline" onClick={copyLink} data-testid="publish-copy-btn">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Warning for unpublished */}
          {!isPublished && (
            <div className="flex gap-2.5 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-400/90 space-y-1">
                <p className="font-medium">This note will become a public web page</p>
                <p>Anyone with the link can read it. While published, every edit you make will be visible immediately.</p>
              </div>
            </div>
          )}

          {/* Password protection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Password protection
              </Label>
              <Switch
                checked={hasPassword}
                onCheckedChange={setHasPassword}
                data-testid="publish-password-toggle"
              />
            </div>
            {hasPassword && (
              <Input
                type="password"
                placeholder={isPublished && pubInfo.has_password ? 'Enter new password to change' : 'Set a password for viewers'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="text-xs bg-secondary/50"
                data-testid="publish-password-input"
              />
            )}
          </div>

          {/* Expiry */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Auto-unpublish
              </Label>
              <Switch
                checked={expiryEnabled}
                onCheckedChange={setExpiryEnabled}
                data-testid="publish-expiry-toggle"
              />
            </div>
            {expiryEnabled && (
              <Input
                type="datetime-local"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="text-xs bg-secondary/50"
                data-testid="publish-expiry-input"
              />
            )}
          </div>

          {/* Actions */}
          {isPublished ? (
            <div className="flex gap-2 pt-1">
              <Button
                onClick={handleUpdateSettings}
                disabled={publishing}
                className="flex-1"
                size="sm"
                data-testid="publish-update-btn"
              >
                {publishing ? 'Saving...' : 'Save Settings'}
              </Button>
              <Button
                onClick={handleUnpublish}
                disabled={publishing}
                variant="destructive"
                size="sm"
                data-testid="publish-unpublish-btn"
              >
                Unpublish
              </Button>
            </div>
          ) : (
            <Button
              onClick={handlePublish}
              disabled={publishing}
              className="w-full"
              data-testid="publish-confirm-btn"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Globe className="w-4 h-4 mr-2" />}
              Publish Note
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
